import moment from 'moment';
import hash from 'object-hash';

import { type IParams } from './IRequestor';

interface ICacheMatchOptions {
  ignoreSearch: boolean;
  ignoreMethod: boolean;
  ignoreVary: boolean;
}

export const isToday = ({ date }: { date: string | number }) =>
  moment(date).isSame(moment(), 'day');

export const isDatePast = ({ date }: { date: string | number }) => {
  const endDate = moment(date);
  const today = moment().startOf('day');
  return endDate.isBefore(today, 'day');
};

export const expiresIn = (amount: number) => {
  return {
    minutes: () => {
      const expiryDate = new Date();
      return expiryDate.setMinutes(expiryDate.getMinutes() + amount);
    },
    hours: () => {
      const expiryDate = new Date();
      return expiryDate.setHours(expiryDate.getHours() + amount);
    },
    days: () => {
      const expiryDate = new Date();
      return expiryDate.setDate(expiryDate.getDate() + amount);
    },
    months: () => {
      const expiryDate = new Date();
      return expiryDate.setMonth(expiryDate.getMonth() + amount);
    },
    years: () => {
      const expiryDate = new Date();
      return expiryDate.setFullYear(expiryDate.getFullYear() + amount);
    },
  };
};

class AppCache {
  private CACHE_KEY = 'vd-app-cache';

  private cache: any;

  private isInit: boolean = false;
  private enabled: boolean = false;

  constructor() {
    this.clear().then(() => this._init());
  }

  configure(useAppCache: boolean) {
    this.enabled = useAppCache;
  }

  hasCache() {
    return typeof self !== 'undefined' && 'caches' in self && this.enabled;
  }

  async invalidate({ request }: { request: string }) {
    const cache: Cache = await this._init();
    if (!cache) {
      return;
    }
    return cache.delete(request, {
      ignoreSearch: true,
      ignoreMethod: true,
      ignoreVary: true,
    });
  }

  async isCached({ cached }: { cached?: Response | any }) {
    const output: any = {
      data: null,
      isCached: false,
    };
    if (!cached || !cached.json) {
      return output;
    }
    const json: any = await cached.json();
    if (json.data && !this._hasExpired({ expires: json.expires })) {
      output.data = json.data;
      output.isCached = true;
    }
    return output;
  }

  async get({
    options,
    params,
    request,
  }: {
    options?: ICacheMatchOptions;
    params: IParams;
    request: string;
  }) {
    const cache: Cache = await this._init();
    if (!cache) {
      return;
    }
    return cache.match(this._getHashedRequest({ request, params }), options);
  }

  async put({
    params,
    request,
    response,
  }: {
    params: IParams;
    request: string;
    response: Response;
  }) {
    const cache: Cache = await this._init();
    if (!cache) {
      return;
    }
    return cache.put(this._getHashedRequest({ request, params }), response);
  }

  async delete({
    options,
    params,
    request,
  }: {
    options?: Record<string, unknown>;
    params: IParams;
    request: string;
  }) {
    const cache: Cache = await this._init();
    if (!cache) {
      return;
    }
    return cache.delete(this._getHashedRequest({ request, params }), options);
  }

  async getKeys() {
    const cache: Cache = await this._init();
    if (!cache) {
      return;
    }
    return cache.keys();
  }

  async clear() {
    this.isInit = false;
    if (this.hasCache()) {
      return caches.delete(this.CACHE_KEY);
    }
    return;
  }

  private async _init() {
    if (!this.isInit && this.hasCache()) {
      this.cache = await caches.open(this.CACHE_KEY);
      this.isInit = true;
    }
    return this.cache;
  }

  private _hasExpired({ expires }: { expires: number }) {
    const now: number = new Date().getTime();
    return expires <= now;
  }

  private _getHashedRequest({ request, params }: { request: string; params: IParams }) {
    if (params?.expires) {
      delete params.expires;
    }
    return request.includes('?')
      ? `${request}&hash=${hash(params)}`
      : `${request}?hash=${hash(params)}`;
  }
}

export default new AppCache();
